/**
 * Utilities for translating between the week-filter display label and the bare
 * week number used in backend query fragments (`week:'<value>'`).
 *
 * The `/api/timemeta/hierarchy/weekno` endpoint returns labels of the form
 * `"202501 (week end: 2024-03-07)"`. The dropdown displays this label as-is, but
 * the backend only understands the bare week number (`"202501"`).
 */

/**
 * Extracts the bare week number from a week label or stored filter value.
 *
 * `"202501 (week end: 2024-03-07)"` -> `"202501"`
 * `"202501"`                        -> `"202501"` (backwards compatible)
 * `"'202501'"`                      -> `"202501"` (values restored from a saved
 *                                                  board carry surrounding quotes)
 */
export function toWeekNo(value: string): string {
    return value
        .replace(/'/g, '')
        .split(' (')[0]
        .trim();
}

/**
 * Normalises a selected week value (single or array) into the comma-joined bare
 * week numbers expected in a `week:'...'` query fragment.
 */
export function normalizeWeekFilter(value: string | Array<string>): string {
    return Array.isArray(value) ? value.map(toWeekNo).join(',') : toWeekNo(value);
}

/**
 * Maps a bare week number back to its full display label when a matching option
 * exists in the supplied options list; otherwise returns the original value.
 *
 * Used when restoring saved boards (which persist the bare week number) so the
 * dropdown can pre-select the full label.
 */
export function toWeekLabel(weekNo: string, options: Array<string>): string {
    return options.find((option: string) => toWeekNo(option) === toWeekNo(weekNo)) ?? weekNo;
}
